import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Eye, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { EmptyState, PageLoader, TableSkeleton } from '@/components/ui/States';
import { Pagination } from '@/components/ui/Table';
import { formatDateTime, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Penampil jejak audit.
 *
 * Catatannya sudah ditulis sejak lama, tetapi tidak pernah ada layar untuk
 * membacanya. Jejak audit yang hanya bisa dibuka lewat mongosh sama saja
 * dengan tidak ada: yang perlu membacanya justru saat sedang panik, dan saat
 * itu tidak ada yang mau mengetik agregasi.
 *
 * Barisnya bisa dibuka satu per satu. Ringkasannya cukup untuk menelusuri,
 * dan rinciannya (nilai sebelum dan sesudah, peramban, alamat) muncul hanya
 * ketika memang dibutuhkan, supaya tabelnya tetap bisa dipindai cepat.
 */

/** Warna per kategori, supaya mata bisa mengelompokkan tanpa membaca. */
const NADA_KATEGORI = {
  user: 'info',
  session: 'info',
  platform: 'brand',
  setting: 'brand',
  transaction: 'success',
  plan: 'success',
  addon: 'success',
  coupon: 'success',
  job: 'accent',
  dataset: 'accent',
  file: 'accent',
  apikey: 'warning',
  bug: 'neutral',
  template: 'neutral',
};

const HARI = [
  [1, 'Hari ini'],
  [7, '7 hari terakhir'],
  [30, '30 hari terakhir'],
  [90, '90 hari terakhir'],
];

const KOSONG = {
  kategori: '',
  aktor: '',
  status: '',
  dari: '',
  sampai: '',
};

const Angka = ({ icon: Icon, label, nilai, catatan, nada }) => (
  <Card>
    <CardBody className="space-y-1">
      <p className="flex items-center gap-2 text-xs font-semibold text-[var(--text-muted)]">
        <Icon className="size-3.5" aria-hidden="true" />
        {label}
      </p>
      <p className={cn('text-2xl font-bold', nada ?? 'text-[var(--text-primary)]')}>{nilai}</p>
      {catatan && <p className="text-xs text-[var(--text-secondary)]">{catatan}</p>}
    </CardBody>
  </Card>
);

/** Menampilkan objek perubahan atau metadata sebagai daftar yang bisa dibaca. */
const Rincian = ({ judul, isi }) => {
  if (!isi || Object.keys(isi).length === 0) return null;
  return (
    <div>
      <p className="text-[11px] font-bold tracking-wide text-[var(--text-muted)] uppercase">
        {judul}
      </p>
      <dl className="mt-1 space-y-1">
        {Object.entries(isi).map(([kunci, nilai]) => {
          // Bentuk { from, to } berasal dari diffChanges. Ditampilkan sebagai
          // panah supaya arah perubahannya langsung terbaca.
          const perubahan =
            nilai && typeof nilai === 'object' && ('from' in nilai || 'to' in nilai);
          return (
            <div key={kunci} className="flex flex-wrap gap-x-2 text-xs">
              <dt className="font-semibold text-[var(--text-secondary)]">{kunci}</dt>
              <dd className="font-mono break-all text-[var(--text-primary)]">
                {perubahan ? (
                  <>
                    <span className="text-danger-600">{JSON.stringify(nilai.from)}</span>
                    <span className="mx-1 text-[var(--text-muted)]">jadi</span>
                    <span className="text-success-600">{JSON.stringify(nilai.to)}</span>
                  </>
                ) : (
                  JSON.stringify(nilai)
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
};

const Baris = ({ b, terbuka, onToggle }) => (
  <>
    <tr
      className="cursor-pointer hover:bg-[var(--surface-sunken)]"
      onClick={onToggle}
      aria-expanded={terbuka}
    >
      <td className="w-8 py-2 pl-3">
        {terbuka ? (
          <ChevronDown className="size-4 text-[var(--text-muted)]" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4 text-[var(--text-muted)]" aria-hidden="true" />
        )}
      </td>
      <td className="py-2 pr-3 text-xs whitespace-nowrap text-[var(--text-secondary)]">
        {formatDateTime(b.waktu)}
      </td>
      <td className="py-2 pr-3">
        <Badge size="sm" tone={NADA_KATEGORI[b.kategori] ?? 'neutral'}>
          {b.tindakan}
        </Badge>
      </td>
      <td className="py-2 pr-3 text-sm text-[var(--text-primary)]">
        {b.aktor ?? <span className="text-[var(--text-muted)]">Sistem</span>}
      </td>
      <td className="py-2 pr-3 text-sm text-[var(--text-secondary)]">
        {b.sasaran?.label ?? b.sasaran?.jenis ?? '-'}
      </td>
      <td className="py-2 pr-3 text-sm text-[var(--text-secondary)]">
        {b.organisasi?.nama ?? '-'}
      </td>
      <td className="py-2 pr-3">
        {b.status === 'failure' ? (
          <Badge size="sm" tone="danger">
            Gagal
          </Badge>
        ) : (
          <span className="text-xs text-[var(--text-muted)]">Berhasil</span>
        )}
      </td>
    </tr>

    {terbuka && (
      <tr>
        <td colSpan={7} className="bg-[var(--surface-sunken)] px-3 py-3">
          <div className="space-y-3">
            <Rincian judul="Perubahan" isi={b.perubahan} />
            <Rincian judul="Keterangan" isi={b.keterangan} />
            <div className="grid gap-2 text-xs sm:grid-cols-2">
              <p>
                <span className="text-[var(--text-muted)]">Alamat: </span>
                <span className="font-mono text-[var(--text-primary)]">{b.ip ?? '-'}</span>
                {b.ipTersamar && b.ip && (
                  <span className="ml-2 text-[var(--text-muted)]">
                    (disamarkan, hanya root yang melihat utuh)
                  </span>
                )}
              </p>
              <p className="break-all">
                <span className="text-[var(--text-muted)]">Peramban: </span>
                <span className="text-[var(--text-primary)]">{b.peramban ?? '-'}</span>
              </p>
              {b.sasaran?.id && (
                <p>
                  <span className="text-[var(--text-muted)]">ID sasaran: </span>
                  <span className="font-mono text-[var(--text-primary)]">{b.sasaran.id}</span>
                </p>
              )}
              <p>
                <span className="text-[var(--text-muted)]">ID catatan: </span>
                <span className="font-mono text-[var(--text-primary)]">{b.id}</span>
              </p>
            </div>
            {!b.perubahan && !b.keterangan && (
              <p className="text-xs text-[var(--text-muted)]">
                Tindakan ini tidak menyimpan rincian tambahan.
              </p>
            )}
          </div>
        </td>
      </tr>
    )}
  </>
);

// -- Halaman --------------------------------------------------------------

const AuditAdminPage = () => {
  const [f, setF] = useState(KOSONG);
  const [page, setPage] = useState(1);
  const [hari, setHari] = useState(7);
  const [terbuka, setTerbuka] = useState(null);

  const params = new URLSearchParams({ page: String(page), limit: '50' });
  for (const [k, v] of Object.entries(f)) if (v) params.set(k, v);

  const ringkasan = useQuery({
    queryKey: ['platform', 'audit', 'summary', hari],
    queryFn: () => api.get(`/platform/audit/summary?hari=${hari}`),
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['platform', 'audit', params.toString()],
    queryFn: () => api.get(`/platform/audit?${params.toString()}`),
    placeholderData: (sebelumnya) => sebelumnya,
  });

  const ubah = (kunci) => (e) => {
    setF((s) => ({ ...s, [kunci]: e.target.value }));
    setPage(1);
    setTerbuka(null);
  };

  const adaSaringan = Object.values(f).some(Boolean);
  const items = data?.data ?? [];
  const meta = data?.meta ?? {};
  const r = ringkasan.data?.data;

  if (ringkasan.isLoading) return <PageLoader label="Memuat jejak audit..." />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Jejak Audit</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Siapa melakukan apa, kapan, dan dari mana. Catatannya hanya bisa ditambah, tidak bisa
            diubah atau dihapus siapa pun lewat aplikasi, dan disimpan dua tahun.
          </p>
        </div>
        <Select
          value={String(hari)}
          onChange={(e) => setHari(Number(e.target.value))}
          className="max-w-[190px]"
        >
          {HARI.map(([nilai, label]) => (
            <option key={nilai} value={nilai}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Angka
          icon={ShieldCheck}
          label="Tindakan tercatat"
          nilai={formatNumber(r?.total ?? 0)}
          catatan={`Dalam ${r?.hari ?? hari} hari terakhir`}
        />
        <Angka
          icon={ShieldAlert}
          label="Percobaan masuk gagal"
          nilai={formatNumber(r?.masukGagal ?? 0)}
          nada={(r?.masukGagal ?? 0) > 20 ? 'text-danger-600' : undefined}
          catatan="Lonjakan mendadak berarti ada yang menebak kata sandi"
        />
        <Angka
          icon={Eye}
          label="Tindakan platform"
          nilai={formatNumber(r?.tindakanPlatform ?? 0)}
          catatan="Perubahan paket, peran, pengaturan, dan akun"
        />
        <Angka
          icon={ShieldAlert}
          label="Tindakan gagal"
          nilai={formatNumber(r?.gagal ?? 0)}
          catatan="Termasuk percobaan yang ditolak izinnya"
        />
      </div>

      {r?.aktorTeraktif?.length > 0 && (
        <Card>
          <CardHeader
            title="Paling banyak beraktivitas"
            description={`Lima akun dengan tindakan terbanyak dalam ${r.hari} hari terakhir.`}
          />
          <CardBody className="flex flex-wrap gap-2">
            {r.aktorTeraktif.map((a) => (
              <button
                key={a.email}
                type="button"
                onClick={() => {
                  setF({ ...KOSONG, aktor: a.email });
                  setPage(1);
                }}
                className="rounded-full border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
              >
                {a.email}
                <span className="ml-1.5 text-[var(--text-muted)]">{formatNumber(a.jumlah)}</span>
              </button>
            ))}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Semua catatan"
          description={
            meta.total !== undefined
              ? `${formatNumber(meta.total)} catatan cocok dengan saringan yang aktif.`
              : undefined
          }
          action={
            adaSaringan && (
              <Button
                size="sm"
                variant="ghost"
                icon={X}
                onClick={() => {
                  setF(KOSONG);
                  setPage(1);
                }}
              >
                Bersihkan saringan
              </Button>
            )
          }
        />
        <CardBody className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Select value={f.kategori} onChange={ubah('kategori')}>
              <option value="">Semua kategori</option>
              {(r?.kategoriTersedia ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </Select>
            <Input placeholder="Email pelaku" value={f.aktor} onChange={ubah('aktor')} />
            <Select value={f.status} onChange={ubah('status')}>
              <option value="">Semua status</option>
              <option value="success">Berhasil</option>
              <option value="failure">Gagal</option>
            </Select>
            <Input type="date" value={f.dari} onChange={ubah('dari')} aria-label="Dari tanggal" />
            <Input
              type="date"
              value={f.sampai}
              onChange={ubah('sampai')}
              aria-label="Sampai tanggal"
            />
          </div>

          {isLoading ? (
            <TableSkeleton rows={8} columns={6} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="Tidak ada catatan yang cocok"
              description="Longgarkan saringannya, atau lebarkan rentang tanggalnya."
            />
          ) : (
            <>
              <div className={cn('overflow-x-auto', isFetching && 'opacity-60')}>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border-subtle)]">
                      <th className="w-8" />
                      {['Waktu', 'Tindakan', 'Pelaku', 'Sasaran', 'Ruang kerja', 'Status'].map(
                        (k) => (
                          <th
                            key={k}
                            className="pb-2 pr-3 text-left text-xs font-semibold text-[var(--text-muted)]"
                          >
                            {k}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {items.map((b) => (
                      <Baris
                        key={b.id}
                        b={b}
                        terbuka={terbuka === b.id}
                        onToggle={() => setTerbuka(terbuka === b.id ? null : b.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={meta.page ?? page}
                totalPages={meta.totalPages ?? 1}
                total={meta.total ?? items.length}
                limit={meta.limit ?? 50}
                onPageChange={(n) => {
                  setPage(n);
                  setTerbuka(null);
                }}
              />

              {meta.bolehLihatIp === false && (
                <p className="text-xs text-[var(--text-muted)]">
                  Alamat IP ditampilkan dalam bentuk tersamar. IP adalah data pribadi pengunjung,
                  jadi bentuk utuhnya hanya terbuka untuk pemilik (root).
                </p>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default AuditAdminPage;
